import type { IBasePropsState } from '@src/slices/base-props.slice'
import { http } from '@providers'
import { notificationAlert } from '@src/modules/atoms/notification'

const handleError = (error: any, showError = true) => {
  if (!showError) return
  notificationAlert({
    message: 'Ha ocurrido un error',
    description: 'Por favor comuniquese con el soporte de Rankmi',
    variant: 'error'
  })

  throw error
}

const getTemplates = async ({ baseUrl, context, resource }: IBasePropsState, lang?: string) => {
  return await http
    .get(`${baseUrl}/rankmi-templates/${context}/workspace/templates`, {
      params: {
        resource,
        lang
      }
    })
    .then(({ data }) => data)
    .catch(handleError)
}

const getCustomTemplates = async (url: string) => {
  return await http
    .get(url)
    .then(({ data }) => data)
    .catch(handleError)
}

const getDocumentTypes = async ( baseUrl: string, q: any ) => {
  const queryParameters = new URLSearchParams(q).toString();
  return await http
    .get(`${baseUrl}/document_types?${queryParameters}`)
    .then(({ data }) => data)
    .catch((error) => handleError(error, false))
}

const getTemplatesByWorkspace = async (
  { baseUrl, context, resource }: IBasePropsState,
  workspace: string,
  lang?: string
) => {
  return await http
    .get(`${baseUrl}/rankmi-templates/${context}/workspace/${workspace}/templates`, {
      params: {
        resource,
        lang
      }
    })
    .then(({ data }) => data)
    .catch(handleError)
}

const getTemplateByToken = async (
  { baseUrl, context, resource }: IBasePropsState,
  templateToken: string,
  lang?: string
) => {
  return await http
    .get(`${baseUrl}/rankmi-templates/${context}/workspace/template/${templateToken}`, {
      params: {
        resource,
        lang
      }
    })
    .then(({ data }) => data)
    .catch(handleError)
}

const createTemplate = async (
  { baseUrl, context, resource }: IBasePropsState,
  template: {
    title: string
    workspace: string
    content: string
    sign_order: JSON | null | undefined
    tags: string[]
  },
  lang?: string
) => {
  return await http
    .post(
      `${baseUrl}/rankmi-templates/${context}/workspace/template/create/`,
      {
        template
      },
      {
        params: {
          resource,
          lang
        }
      }
    )
    .then(({ data }) => data)
    .catch(handleError)
}

const updateTemplate = async (
  { baseUrl, context, resource }: IBasePropsState,
  templateToken: string,
  template: {
    title: string
    workspace: string
    content: string
    sign_order: JSON | null | undefined
    tags: string[]
  },
  lang?: string
) => {
  return await http
    .put(
      `${baseUrl}/rankmi-templates/${context}/workspace/template/${templateToken}/`,
      {
        template
      },
      {
        params: {
          resource,
          lang
        }
      }
    )
    .then(({ data }) => data)
    .catch(handleError)
}

const deleteTemplate = async (
  { baseUrl, context, resource }: IBasePropsState,
  templateToken: string,
  lang?: string
) => {
  return await http
    .delete(`${baseUrl}/rankmi-templates/${context}/workspace/template/${templateToken}/`, {
      params: {
        resource,
        lang
      }
    })
    .then(({ data }) => data)
    .catch(handleError)
}

const changeTemplateState = async (
  { baseUrl, context, resource }: IBasePropsState,
  templateToken: string,
  lang?: string
) => {
  return await http
    .get(`${baseUrl}/rankmi-templates/${context}/workspace/template/change/${templateToken}/`, {
      params: {
        resource,
        lang
      }
    })
    .then(({ data }) => data)
    .catch(handleError)
}

const getWorkspaces = async ({ baseUrl, context, resource }: IBasePropsState, lang?: string) => {
  return await http
    .get(`${baseUrl}/rankmi-templates/${context}/workspace/list`, {
      params: {
        resource,
        lang
      }
    })
    .then(({ data }) => data)
    .catch(handleError)
}

const getVariables = async (
  { baseUrl, context, resource }: IBasePropsState,
  workspace: string,
  lang?: string
) => {
  return await http
    .get(`${baseUrl}/rankmi-templates/${context}/workspace/variables`, {
      params: { workspace, resource, lang }
    })
    .then(({ data }) => data)
    .catch(handleError)
}

const getPdfLink = async (
  { baseUrl, context, resource }: IBasePropsState,
  workspace: string,
  templateToken: string,
  queryParams: any,
  lang?: string
) => {
  return await http
    .get(
      `${baseUrl}/rankmi-templates/${context}/workspace/${workspace}/template/${templateToken}`,
      {
        params: { resource, lang, ...queryParams }
      }
    )
    .then(({ data }) => data)
    .catch(handleError)
}

const getPreview = async (
  { baseUrl, context, resource }: IBasePropsState,
  workspace: string,
  content: string,
  lang?: string
) => {
  return await http
    .post(
      `${baseUrl}/rankmi-templates/${context}/workspace/${workspace}/template/preview`,
      {
        template: { content }
      },
      {
        params: {
          resource,
          lang,
          format: 'pdf'
        },
        responseType: 'blob'
      }
    )
    .then(({ data }) => data)
    .catch(handleError)
}

export {
  getTemplates,
  getCustomTemplates,
  getTemplatesByWorkspace,
  getTemplateByToken,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  changeTemplateState,
  getWorkspaces,
  getVariables,
  getPdfLink,
  getPreview,
  getDocumentTypes
}
